// In src/services/budgetService.ts
import axiosInstance from '../utils/axiosConfig';
import { PerformanceBudget, PerformanceBudgetItem } from '../types/PurchaseOrder';
import { Performance, FullCosts } from '../components/performance/form/types'; // Use your actual import path

export const budgetService = {
  // Get budget for a performance
  async getBudgetByPerformance(performanceId: string): Promise<PerformanceBudget> {
    try {
      // First try to get dedicated budget
      const response = await axiosInstance.get(`/api/performance-budgets/performance/${performanceId}`);
      return response.data;
    } catch (error) {
      // If budget not found, try to extract from performance itself
      const performanceResponse = await axiosInstance.get(`/api/performances/${performanceId}`);
      const performance = performanceResponse.data as Performance;
      
      // Create budget items from performance.costs
      const budgetItems: PerformanceBudgetItem[] = [];
      const costs = performance.costs as FullCosts;
      
      // Band fee
      if (costs?.bandFee) {
        budgetItems.push({
          _id: 'band-fee',
          category: 'artist',
          description: 'Band Fee',
          budgetedAmount: costs.bandFee,
          isContingency: false
        });
      }
      
      // Technical costs
      if (costs?.technical) {
        const technical = costs.technical;
        if (technical.soundEngineer) {
          budgetItems.push({
            _id: 'sound-engineer',
            category: 'technical',
            description: 'Sound Engineer',
            budgetedAmount: technical.soundEngineer,
            isContingency: false
          });
        }
        if (technical.lightingEngineer) {
          budgetItems.push({
            _id: 'lighting-engineer',
            category: 'technical',
            description: 'Lighting Engineer',
            budgetedAmount: technical.lightingEngineer,
            isContingency: false
          });
        }
        if (technical.backline) {
          budgetItems.push({
            _id: 'backline',
            category: 'technical',
            description: 'Backline',
            budgetedAmount: technical.backline,
            isContingency: false
          });
        }
        if (technical.other) {
          budgetItems.push({
            _id: 'technical-other',
            category: 'technical',
            description: 'Other Technical Costs',
            budgetedAmount: technical.other,
            isContingency: false
          });
        }
      }
      
      // Transport costs
      if (costs?.travel) {
        const travel = costs.travel;
        if (travel.flights) {
          budgetItems.push({
            _id: 'flights',
            category: 'transport',
            description: 'Flights',
            budgetedAmount: travel.flights,
            isContingency: false
          });
        }
        if (travel.groundTransport) {
          budgetItems.push({
            _id: 'ground-transport',
            category: 'transport',
            description: 'Ground Transport',
            budgetedAmount: travel.groundTransport,
            isContingency: false
          });
        }
        if (travel.other) {
          budgetItems.push({
            _id: 'travel-other',
            category: 'transport',
            description: 'Other Travel Costs',
            budgetedAmount: travel.other,
            isContingency: false
          });
        }
      }
      
      // Accommodation
      if (costs?.accommodation) {
        const accommodation = costs.accommodation;
        // Using proper nested object structure based on your FullCosts type
        const singleRoomCost = 
          (accommodation.singleRooms.quantity || 0) * 
          (accommodation.singleRooms.costPerRoom || 0) * 
          (accommodation.singleRooms.totalNights || 1);
          
        const twinRoomCost = 
          (accommodation.twinRooms.quantity || 0) * 
          (accommodation.twinRooms.costPerRoom || 0) * 
          (accommodation.twinRooms.totalNights || 1);
          
        const totalAccommodation = singleRoomCost + twinRoomCost;
          
        if (totalAccommodation > 0) {
          budgetItems.push({
            _id: 'accommodation',
            category: 'other',
            description: 'Accommodation',
            budgetedAmount: totalAccommodation,
            isContingency: false
          });
        }
      }
      
      // Per Diem
      if (costs?.perDiem) {
        const perDiem = costs.perDiem;
        const totalPerDiem = 
          (perDiem.dailyRate || 0) * 
          (perDiem.numberOfDays || 1);
          
        if (totalPerDiem > 0) {
          budgetItems.push({
            _id: 'per-diem',
            category: 'other',
            description: 'Per Diem',
            budgetedAmount: totalPerDiem,
            isContingency: false
          });
        }
      }
      
      // Equipment
      if (costs?.equipment) {
        const equipment = costs.equipment;
        if (equipment.rental) {
          budgetItems.push({
            _id: 'equipment-rental',
            category: 'other',
            description: 'Equipment Rental',
            budgetedAmount: equipment.rental,
            isContingency: false
          });
        }
        if (equipment.shipping) {
          budgetItems.push({
            _id: 'equipment-shipping',
            category: 'other',
            description: 'Equipment Shipping',
            budgetedAmount: equipment.shipping,
            isContingency: false
          });
        }
      }
      
      // Additional costs like marketing
      if (costs?.additional) {
        const additional = costs.additional;
        if (additional.marketing) {
          budgetItems.push({
            _id: 'marketing',
            category: 'marketing',
            description: 'Marketing',
            budgetedAmount: additional.marketing,
            isContingency: false
          });
        }
        if (additional.insurance) {
          budgetItems.push({
            _id: 'insurance',
            category: 'other',
            description: 'Insurance',
            budgetedAmount: additional.insurance,
            isContingency: false
          });
        }
        if (additional.visas) {
          budgetItems.push({
            _id: 'visas',
            category: 'other',
            description: 'Visas',
            budgetedAmount: additional.visas,
            isContingency: false
          });
        }
      }
      
      // Contingency
      if (costs?.summary?.contingency) {
        budgetItems.push({
          _id: 'contingency',
          category: 'contingency',
          description: 'Contingency',
          budgetedAmount: costs.summary.contingency,
          isContingency: true
        });
      }
      
      // Create a PerformanceBudget object from the extracted data
      const budget: PerformanceBudget = {
        _id: `derived-${performanceId}`,
        performanceId,
        items: budgetItems,
        totalBudget: costs?.summary?.totalCosts || 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      return budget;
    }
  },
  
  // Create budget for a performance
  async createBudget(data: {
    performanceId: string;
    items: PerformanceBudgetItem[];
  }): Promise<PerformanceBudget> {
    const response = await axiosInstance.post('/api/performance-budgets', data);
    return response.data;
  },
  
  // Update budget
  async updateBudget(id: string, items: PerformanceBudgetItem[]): Promise<PerformanceBudget> {
    const response = await axiosInstance.put(`/api/performance-budgets/${id}`, { items });
    return response.data;
  },
  
  // Get budget comparison
  async getBudgetComparison(performanceId: string): Promise<any> {
    const response = await axiosInstance.get(`/api/performance-budgets/comparison/${performanceId}`);
    return response.data;
  }
};